import type { Contato, EvidenciaOptin } from '@eleg360/types';
import { formatarData } from '@/lib/format';

/**
 * `optin_evidencia`/`optin_evidencia_ref` já existem na entidade e no DTO da
 * API (`apps/api/src/gab/entities/contato.entity.ts:69-72`) mas nunca
 * entraram no tipo `Contato` compartilhado (`packages/types/src/index.ts`) —
 * `ContatosPage.tsx` já lida com essa lacuna lendo o campo via `as any`. Este
 * card faz o mesmo, em vez de duplicar o tipo.
 */
type ContatoComEvidencia = Contato & {
  optin_evidencia?: EvidenciaOptin;
  optin_evidencia_ref?: string;
};

const ROTULO_EVIDENCIA: Record<EvidenciaOptin, string> = {
  nao_informada: 'Não informada',
  formulario: 'Formulário assinado ou digital',
  whatsapp: 'Mensagem de WhatsApp',
  audio: 'Áudio ou gravação',
  presencial: 'Presencial, com testemunha',
  importacao: 'Importação (prova sob guarda do gabinete)',
};

/**
 * Card "Consentimento (LGPD art. 11)" da ficha do contato.
 *
 * proto:911 é a definição de `DRAWERS.contato` que vale em runtime (a de
 * proto:798 é sobrescrita por essa) — os três textos abaixo (optin/optout/
 * sem_base) são copiados literalmente dela.
 *
 * O protótipo também mostra "Canais" e "Mensagens no mês · X/teto" com
 * valores de demonstração fixos. Não há campo `canal` nem contador de
 * mensagens no `Contato` real (`packages/types/src/index.ts:87-108`) — como a
 * regra desta fase é dado real ou nada, essas duas linhas ficam de fora.
 */
export function CardConsentimento({ contato }: { contato: ContatoComEvidencia }) {
  const evidencia = contato.optin_evidencia ?? 'nao_informada';
  const rotuloEvidencia = ROTULO_EVIDENCIA[evidencia];

  return (
    <div className="card flat">
      <h3>Consentimento (LGPD art. 11)</h3>
      {contato.base_legal === 'optin' && (
        <dl className="dl" style={{ marginTop: 8 }}>
          <dt>Registrado em</dt>
          <dd>{formatarData(contato.optin_ts)}</dd>
          <dt>Evidência</dt>
          <dd>
            {evidencia === 'nao_informada' ? (
              <span className="chip warn">Não informada</span>
            ) : (
              <>
                {rotuloEvidencia}
                {contato.optin_evidencia_ref ? (
                  <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                    {contato.optin_evidencia_ref}
                  </div>
                ) : null}
              </>
            )}
          </dd>
        </dl>
      )}
      {contato.base_legal === 'optout' && (
        <p style={{ fontSize: 13 }}>Revogado em {formatarData(contato.optout_ts)}. Comunicação bloqueada.</p>
      )}
      {contato.base_legal === 'sem_base' && (
        <p style={{ fontSize: 13 }}>
          Sem base legal. <b>Não recebe</b> comunicação até ato explícito do titular.
        </p>
      )}
    </div>
  );
}
